
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Dialog,
  Fade,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useWizardState } from './useWizardState';
import SetupStep from './SetupStep';
import ConfigurationStep from './ConfigurationStep';
import DemandEstimationStep from './DemandEstimationStep';
import HubWeightingStep from './HubWeightingStep';
import FleetSupplyStep from './FleetSupplyStep';
import ServiceFeelStep from './ServiceFeelStep';
import SummaryRunStep from './SummaryRunStep';
import type {
  DemandInsightResult,
  ServicePreset,
  WizardFleetConfigPayload,
  WizardFleetGeneratedShift,
  WizardFleetHelpResult,
  WizardGenerateTripsPayload,
  WizardRunPayload,
  WizardTimelineSlotStatus,
} from './types';
import type { WizardDismissSource, WizardEntrypoint, WizardFieldGroup } from '../../services/eventTypes';
import {
  buildWizardDefaultSnapshot,
  categorizeWizardHelperError,
  createWizardSessionId,
  diffWizardFieldGroups,
  emitWizardDefaultRetained,
  getWizardStepId,
  mapWizardValidationReason,
  trackWizardCompleted,
  trackWizardDismissed,
  trackWizardFieldGroupChanged,
  trackWizardHelperComplete,
  trackWizardHelperFail,
  trackWizardHelperStart,
  trackWizardNavigationClicked,
  trackWizardOpened,
  trackWizardStepCompleted,
  trackWizardStepViewed,
  trackWizardValidationBlocked,
  toServiceMode,
  toServicePreset,
} from './analytics';
import { calculateTimeDifference, parseTime } from '../map/components/auto-fleet-generator/timeUtils';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors } from '../../theme/opsTokens';

interface WizardModalProps {
  open: boolean;
  wizardEntrypoint: WizardEntrypoint;
  polygonCount: number;
  hasWizardServiceAreaPolygon?: boolean;
  polygonDraftVertexCount?: number;
  pointCount: number;
  serviceAreaPolygonLabel: string | null;
  mapInteractionResumeToken?: number;
  onClose: () => void;
  hubOptions: Array<{ id: string; name: string }>;
  onDrawPolygon: () => void;
  onDrawHub: () => void;
  onRequestDemandInsight: () => Promise<DemandInsightResult>;
  onTriggerGenerateTripsBackground: (payload: WizardGenerateTripsPayload) => Promise<string | void>;
  isWizardRideLayerIdUsable?: (rideLayerId: string, requireTimeData?: boolean) => boolean;
  hasWheelchairTripsForRideLayer?: (rideLayerId: string) => boolean;
  onGenerateFleetFromDemand: (payload: {
    authoritativeRideLayerId?: string | null;
    servicePreset?: ServicePreset | null;
    capacityStandard: number;
    capacityWheelchair: number;
  }) => Promise<WizardFleetHelpResult>;
  onCreateManualFleet: (payload: WizardFleetConfigPayload) => Promise<string>;
  onRunSimulation: (payload: WizardRunPayload) => Promise<void>;
  onEditServiceAreaPolygon: () => void;
  onFinishZonePolygon: () => void;
  onDoneMapInteraction: () => void;
  onCancelMapInteraction: () => void;
}

type PendingMapHelper = 'map_polygon_draw' | 'map_hub_draw';
type ActiveDrawTool = 'zone' | 'hub' | null;
const WIZARD_MAX_GENERATED_TRIPS = 250;
const MIN_WIZARD_POLYGON_VERTICES = 3;
const TRIPS_PER_VEHICLE_PER_HOUR_DEFAULT = 5;
const PARATRANSIT_TRIPS_PER_VEHICLE_PER_HOUR_DEFAULT = 3;
const DEMAND_BASE_MULTIPLIER = 0.25;
const PARATRANSIT_DEMAND_MULTIPLIER = 0.6;
const STANDARD_MAX_SHIFT_TIME_MINUTES = 8 * 60;
const FLEET_TIMELINE_INTERVAL_MINUTES = 30;

function getTripsPerVehiclePerHourForPreset(preset: ServicePreset | null): number {
  return preset === 'paratransit' ? PARATRANSIT_TRIPS_PER_VEHICLE_PER_HOUR_DEFAULT : TRIPS_PER_VEHICLE_PER_HOUR_DEFAULT;
}

function getDemandMultiplierForPreset(preset: ServicePreset | null): number {
  return preset === 'paratransit' ? PARATRANSIT_DEMAND_MULTIPLIER : 1;
}

function getFallbackFleetCapacities(preset: ServicePreset | null): { standard: number; wheelchair: number } {
  if (preset === 'paratransit') {
    return { standard: 12, wheelchair: 2 };
  }
  return { standard: 12, wheelchair: 1 };
}

function getDefaultPassengerDistribution(preset: ServicePreset | null): { standardPct: number; wheelchairPct: number } {
  if (preset === 'paratransit') {
    return { standardPct: 90, wheelchairPct: 10 };
  }
  return { standardPct: 98, wheelchairPct: 2 };
}

function getEffectivePassengerDistribution(
  preset: ServicePreset | null,
  distribution: { standardPct: number; wheelchairPct: number } | null | undefined
): { standardPct: number; wheelchairPct: number } {
  const defaults = getDefaultPassengerDistribution(preset);
  if (!distribution) {
    return defaults;
  }

  const standardPct = Math.round(Number(distribution.standardPct));
  const wheelchairPct = Math.round(Number(distribution.wheelchairPct));
  if (!Number.isFinite(standardPct) || !Number.isFinite(wheelchairPct)) {
    return defaults;
  }
  if (standardPct < 0 || wheelchairPct < 0 || standardPct + wheelchairPct !== 100) {
    return defaults;
  }

  return {
    standardPct,
    wheelchairPct,
  };
}

function isValidUtcClock(value: string): boolean {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
}

function toUtcClockMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function formatClockFromMinutes(totalMinutes: number): string {
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function splitOperatingPeriodsForMaxShift(
  periods: Array<{ start: string; end: string; breaks?: Array<{ start: string; end: string; location: string }> }>,
  maxShiftMinutes: number,
  defaultBreakLocation: string
): Array<{ start: string; end: string; breaks: Array<{ start: string; end: string; location: string }> }> {
  const segments: Array<{ start: string; end: string; breaks: Array<{ start: string; end: string; location: string }> }> = [];
  periods.forEach((period) => {
    const periodStart = parseTime(period.start);
    const periodEnd = parseTime(period.end);
    const periodBreaks = period.breaks || [];
    if (!Number.isFinite(periodStart) || !Number.isFinite(periodEnd) || periodEnd <= periodStart) {
      return;
    }
    for (let cursor = periodStart; cursor < periodEnd; cursor += maxShiftMinutes) {
      const segmentEnd = Math.min(periodEnd, cursor + maxShiftMinutes);
      const segmentBreaks = periodBreaks
        .map((breakPeriod) => {
          const breakStart = parseTime(breakPeriod.start);
          const breakEnd = parseTime(breakPeriod.end);
          const overlaps = breakStart < segmentEnd && breakEnd > cursor;
          if (!overlaps) {
            return null;
          }
          return {
            start: formatClockFromMinutes(Math.max(cursor, breakStart)),
            end: formatClockFromMinutes(Math.min(segmentEnd, breakEnd)),
            location: breakPeriod.location || defaultBreakLocation,
          };
        })
        .filter(
          (breakPeriod): breakPeriod is { start: string; end: string; location: string } => {
            if (!breakPeriod) {
              return false;
            }
            return parseTime(breakPeriod.end) > parseTime(breakPeriod.start);
          }
        );
      segments.push({
        start: formatClockFromMinutes(cursor),
        end: formatClockFromMinutes(segmentEnd),
        breaks: segmentBreaks,
      });
    }
  });
  return segments;
}

function getShiftStatusAtBlock(
  shift: WizardFleetGeneratedShift,
  blockStartMinutes: number,
  intervalMinutes: number
): WizardTimelineSlotStatus {
  const blockEndMinutes = blockStartMinutes + intervalMinutes;
  for (const period of shift.operatingPeriods) {
    const periodStart = parseTime(period.start);
    const periodEnd = parseTime(period.end);
    if (blockStartMinutes >= periodStart && blockStartMinutes < periodEnd) {
      for (const breakPeriod of period.breaks || []) {
        const breakStart = parseTime(breakPeriod.start);
        const breakEnd = parseTime(breakPeriod.end);
        if (blockStartMinutes < breakEnd && blockEndMinutes > breakStart) {
          return 'break';
        }
      }
      return 'working';
    }
  }
  return 'off';
}

function buildOperatingPeriodsFromStatuses(
  statuses: WizardTimelineSlotStatus[],
  serviceStartMinutes: number,
  intervalMinutes: number,
  defaultBreakLocation: string
): Array<{ start: string; end: string; breaks: Array<{ start: string; end: string; location: string }> }> {
  const periods: Array<{ start: string; end: string; breaks: Array<{ start: string; end: string; location: string }> }> = [];
  let currentPeriodStart: number | null = null;
  let currentBreakStart: number | null = null;
  let currentBreaks: Array<{ start: string; end: string; location: string }> = [];

  statuses.forEach((status, index) => {
    const blockStart = serviceStartMinutes + index * intervalMinutes;
    const blockEnd = blockStart + intervalMinutes;
    const isOff = status === 'off';
    const isBreak = status === 'break';

    if (!isOff && currentPeriodStart === null) {
      currentPeriodStart = blockStart;
    }
    if (isBreak && currentBreakStart === null) {
      currentBreakStart = blockStart;
    }
    if (!isBreak && currentBreakStart !== null) {
      currentBreaks.push({
        start: formatClockFromMinutes(currentBreakStart),
        end: formatClockFromMinutes(blockStart),
        location: defaultBreakLocation,
      });
      currentBreakStart = null;
    }
    if (isOff && currentPeriodStart !== null) {
      if (currentBreakStart !== null) {
        currentBreaks.push({
          start: formatClockFromMinutes(currentBreakStart),
          end: formatClockFromMinutes(blockStart),
          location: defaultBreakLocation,
        });
        currentBreakStart = null;
      }
      periods.push({
        start: formatClockFromMinutes(currentPeriodStart),
        end: formatClockFromMinutes(blockStart),
        breaks: currentBreaks,
      });
      currentPeriodStart = null;
      currentBreaks = [];
    }

    if (index === statuses.length - 1 && currentPeriodStart !== null) {
      if (currentBreakStart !== null) {
        currentBreaks.push({
          start: formatClockFromMinutes(currentBreakStart),
          end: formatClockFromMinutes(blockEnd),
          location: defaultBreakLocation,
        });
      }
      periods.push({
        start: formatClockFromMinutes(currentPeriodStart),
        end: formatClockFromMinutes(blockEnd),
        breaks: currentBreaks,
      });
    }
  });

  const normalized = periods.filter((period) => parseTime(period.end) > parseTime(period.start));
  return splitOperatingPeriodsForMaxShift(normalized, STANDARD_MAX_SHIFT_TIME_MINUTES, defaultBreakLocation);
}

function toTimelineSlotKey(vehicleId: string, blockStartMinutes: number): string {
  return `${vehicleId}:${blockStartMinutes}`;
}

function getNextTimelineStatus(current: WizardTimelineSlotStatus): WizardTimelineSlotStatus {
  if (current === 'working') return 'break';
  if (current === 'break') return 'off';
  return 'working';
}

function expandShiftPeriodsToDiscreteShifts(
  shifts: WizardFleetGeneratedShift[],
  formatShiftSuffix: (name: string, index: number) => string
): WizardFleetGeneratedShift[] {
  return shifts.flatMap((shift) => {
    const validPeriods = shift.operatingPeriods.filter((period) => parseTime(period.end) > parseTime(period.start));
    return validPeriods.map((period, index) => ({
      vehicleId: `${shift.vehicleId}-segment-${index + 1}`,
      vehicleName: validPeriods.length > 1 ? formatShiftSuffix(shift.vehicleName, index + 1) : shift.vehicleName,
      operatingPeriods: [period],
    }));
  });
}

function buildManualFleetPreview(
  state: ReturnType<typeof useWizardState>['state'],
  labels: { fleetName: string; vehicleName: (index: number) => string; defaultBreakLocation: string }
): WizardFleetHelpResult | null {
  const vehicleCount = Math.max(0, Math.round(Number(state.vehicleCount) || 0));
  if (vehicleCount <= 0) {
    return null;
  }

  const validPeriods = state.operatingPeriodsUtc.filter(
    (period) =>
      isValidUtcClock(period.startUtc) &&
      isValidUtcClock(period.endUtc) &&
      parseTime(period.endUtc) > parseTime(period.startUtc)
  );

  if (validPeriods.length === 0) {
    return null;
  }

  const splitOperatingPeriods = splitOperatingPeriodsForMaxShift(
    validPeriods.map((period) => ({ start: period.startUtc, end: period.endUtc })),
    STANDARD_MAX_SHIFT_TIME_MINUTES,
    labels.defaultBreakLocation
  );
  if (splitOperatingPeriods.length === 0) {
    return null;
  }

  const periodMinutesPerVehicle = splitOperatingPeriods.reduce(
    (sum, period) => sum + calculateTimeDifference(parseTime(period.start), parseTime(period.end)),
    0
  );
  const activeVehicleHours = round1((periodMinutesPerVehicle * vehicleCount) / 60);
  const totalVehicleHours = activeVehicleHours;

  const periodStarts = splitOperatingPeriods.map((period) => parseTime(period.start));
  const periodEnds = splitOperatingPeriods.map((period) => parseTime(period.end));
  const serviceStartMinutes = Math.min(...periodStarts);
  const serviceEndMinutes = Math.max(...periodEnds);
  const serviceStartTime =
    splitOperatingPeriods.find((period) => parseTime(period.start) === serviceStartMinutes)?.start ?? '06:00';
  const serviceEndTime =
    splitOperatingPeriods.find((period) => parseTime(period.end) === serviceEndMinutes)?.end ?? '22:00';

  return {
    fleetId: 'manual-preview',
    fleetName: labels.fleetName,
    summary: {
      totalVehicles: vehicleCount,
      totalShifts: vehicleCount * splitOperatingPeriods.length,
      totalBreaks: 0,
      servicePeriod: `${serviceStartTime} - ${serviceEndTime}`,
      totalStandardSeats: vehicleCount * Math.max(0, Math.round(Number(state.capacityStandard) || 0)),
      totalWheelchairSeats: vehicleCount * Math.max(0, Math.round(Number(state.capacityWheelchair) || 0)),
      totalCapacity:
        vehicleCount *
        (Math.max(0, Math.round(Number(state.capacityStandard) || 0)) +
          Math.max(0, Math.round(Number(state.capacityWheelchair) || 0))),
      activeVehicleHours,
      totalVehicleHours,
      utilizationPercent: totalVehicleHours > 0 ? 100 : 0,
    },
    shifts: Array.from({ length: vehicleCount }, (_, index) => ({
      vehicleId: `manual-preview-vehicle-${index + 1}`,
      vehicleName: labels.vehicleName(index + 1),
      operatingPeriods: splitOperatingPeriods.map((period) => ({
        start: period.start,
        end: period.end,
        breaks: period.breaks,
      })),
    })),
    serviceStartTime,
    serviceEndTime,
    peakPeriods: [],
  };
}

export default function WizardModal({
  open,
  wizardEntrypoint,
  polygonCount,
  hasWizardServiceAreaPolygon = false,
  polygonDraftVertexCount = 0,
  pointCount,
  serviceAreaPolygonLabel,
  mapInteractionResumeToken = 0,
  onClose,
  hubOptions,
  onDrawPolygon,
  onDrawHub,
  onRequestDemandInsight,
  onTriggerGenerateTripsBackground,
  isWizardRideLayerIdUsable,
  hasWheelchairTripsForRideLayer,
  onGenerateFleetFromDemand,
  onCreateManualFleet,
  onRunSimulation,
  onEditServiceAreaPolygon,
  onFinishZonePolygon,
  onDoneMapInteraction,
  onCancelMapInteraction,
}: WizardModalProps) {
  const { t } = useTranslation('onboarding');
  const wizard = useWizardState(polygonCount, hasWheelchairTripsForRideLayer);
  const { state } = wizard;
  const [isRunLoading, setIsRunLoading] = useState(false);
  const [activeDrawTool, setActiveDrawTool] = useState<ActiveDrawTool>(null);
  const [fleetHelpError, setFleetHelpError] = useState<string | null>(null);
  const [fleetHelpResult, setFleetHelpResult] = useState<WizardFleetHelpResult | null>(null);
  const [hubPlacementMessage, setHubPlacementMessage] = useState<string | null>(null);
  const [demandLimitFocusSignal, setDemandLimitFocusSignal] = useState(0);
  const [showDemandLimitAttention, setShowDemandLimitAttention] = useState(false);
  const demandOverlayInputRef = useRef<HTMLInputElement | null>(null);
  const lastResumeTokenRef = useRef(mapInteractionResumeToken);
  const requiresHubs =
    state.servicePreset !== 'paratransit' && (state.serviceMode === 'door_to_hub' || state.serviceMode === 'hub_to_hub');

  const wizardSessionIdRef = useRef<string | null>(null);
  const wizardOpenedAtRef = useRef<number | null>(null);
  const stepEnteredAtRef = useRef<number | null>(null);
  const lastStepViewKeyRef = useRef<string | null>(null);
  const defaultSnapshotRef = useRef<ReturnType<typeof buildWizardDefaultSnapshot> | null>(null);
  const changedFieldGroupsRef = useRef<Set<WizardFieldGroup>>(new Set());
  const initialSimulationNameRef = useRef<string>('');
  const prevOpenRef = useRef(false);
  const pendingMapHelperRef = useRef<PendingMapHelper | null>(null);
  const demandHelperUsedRef = useRef(false);
  const entrypointRef = useRef<WizardEntrypoint>(wizardEntrypoint);
  const wizardStateRef = useRef(state);
  const polygonCountRef = useRef(polygonCount);
  const schoolConfigAutoMapStartedRef = useRef(false);

  const progressLabel = useMemo(
    () => t('wizard.progress', { current: state.currentStep, total: wizard.totalSteps }),
    [state.currentStep, wizard.totalSteps, t]
  );
  const currentStepId = wizard.currentStepId;
  const isDemandOverlayStep = open && currentStepId === 'demand' && !state.isMinimizedForMap;
  const exceedsDemandTripLimit = typeof state.demandTrips === 'number' && state.demandTrips > WIZARD_MAX_GENERATED_TRIPS;
  const canProceedToHubPlacement =
    polygonCount > 0 || hasWizardServiceAreaPolygon || polygonDraftVertexCount >= MIN_WIZARD_POLYGON_VERTICES;
  const showDefaultHubPlacementGuidance =
    currentStepId === 'configuration' && requiresHubs && !hasWizardServiceAreaPolygon && activeDrawTool !== 'hub';
  const showHubPlacementGuidance =
    currentStepId === 'configuration' && (Boolean(hubPlacementMessage) || showDefaultHubPlacementGuidance);
  const hubPlacementGuidanceText =
    hubPlacementMessage ??
    t('hubGuidance.default', { minVertices: MIN_WIZARD_POLYGON_VERTICES });
  const manualFleetPreview = useMemo(() => {
    if (state.fleetSource === 'wizard_helper' && state.authoritativeFleetId) {
      return null;
    }
    return buildManualFleetPreview(state, {
      fleetName: t('fleet.manualPreviewFleetName'),
      vehicleName: (index: number) => t('fleet.manualVehicleName', { index }),
      defaultBreakLocation: t('fleet.onRouteBreakLocation'),
    });
  }, [state, t]);
  const manualTimelineSignature = useMemo(() => {
    if (!manualFleetPreview) return '';
    const shiftSignature = manualFleetPreview.shifts
      .map((shift) =>
        `${shift.vehicleId}:${shift.operatingPeriods.map((period) => `${period.start}-${period.end}`).join(',')}`
      )
      .join('|');
    return `${manualFleetPreview.serviceStartTime}-${manualFleetPreview.serviceEndTime}-${shiftSignature}`;
  }, [manualFleetPreview]);
  const editableManualFleetPreview = useMemo(() => {
    if (!manualFleetPreview) {
      return null;
    }
    const serviceStartMinutes = parseTime(manualFleetPreview.serviceStartTime);
    const serviceEndMinutes = parseTime(manualFleetPreview.serviceEndTime);
    const totalBlocks = Math.max(0, Math.ceil((serviceEndMinutes - serviceStartMinutes) / FLEET_TIMELINE_INTERVAL_MINUTES));

    const shifts = manualFleetPreview.shifts.map((shift) => {
      const statuses: WizardTimelineSlotStatus[] = Array.from({ length: totalBlocks }, (_, blockIndex) => {
        const blockStartMinutes = serviceStartMinutes + blockIndex * FLEET_TIMELINE_INTERVAL_MINUTES;
        const slotKey = toTimelineSlotKey(shift.vehicleId, blockStartMinutes);
        return state.fleetTimelineSlotStatuses[slotKey] ?? getShiftStatusAtBlock(shift, blockStartMinutes, FLEET_TIMELINE_INTERVAL_MINUTES);
      });
      return {
        ...shift,
        operatingPeriods: buildOperatingPeriodsFromStatuses(
          statuses,
          serviceStartMinutes,
          FLEET_TIMELINE_INTERVAL_MINUTES,
          t('fleet.onRouteBreakLocation')
        ),
      };
    });

    const totalBreaks = shifts.reduce(
      (sum, shift) => sum + shift.operatingPeriods.reduce((inner, period) => inner + (period.breaks?.length || 0), 0),
      0
    );
    const activeMinutes = shifts.reduce((sum, shift) => {
      const shiftMinutes = shift.operatingPeriods.reduce((inner, period) => {
        const periodMinutes = calculateTimeDifference(parseTime(period.start), parseTime(period.end));
        const breakMinutes = (period.breaks || []).reduce(
          (breakSum, breakPeriod) => breakSum + calculateTimeDifference(parseTime(breakPeriod.start), parseTime(breakPeriod.end)),
          0
        );
        return inner + Math.max(0, periodMinutes - breakMinutes);
      }, 0);
      return sum + shiftMinutes;
    }, 0);
    const activeVehicleHours = round1(activeMinutes / 60);

    return {
      ...manualFleetPreview,
      shifts,
      summary: {
        ...manualFleetPreview.summary,
        totalShifts: shifts.reduce((sum, shift) => sum + shift.operatingPeriods.length, 0),
        totalBreaks,
        activeVehicleHours,
        utilizationPercent:
          manualFleetPreview.summary.totalVehicleHours > 0
            ? round1((activeVehicleHours / manualFleetPreview.summary.totalVehicleHours) * 100)
            : 0,
      },
    };
  }, [manualFleetPreview, state.fleetTimelineSlotStatuses, t]);
  const handleTimelineSlotToggle = useCallback(
    (vehicleId: string, blockStartMinutes: number, status: WizardTimelineSlotStatus) => {
      const slotKey = toTimelineSlotKey(vehicleId, blockStartMinutes);
      wizard.setFleetTimelineSlotStatus(slotKey, getNextTimelineStatus(status));
    },
    [wizard]
  );

  const demandServiceStartUtc = useMemo(() => {
    const validStarts = state.operatingPeriodsUtc
      .map((period) => period.startUtc)
      .filter((time) => isValidUtcClock(time))
      .sort();
    return validStarts[0] ?? '06:00';
  }, [state.operatingPeriodsUtc]);

  const demandServiceEndUtc = useMemo(() => {
    const validEnds = state.operatingPeriodsUtc
      .map((period) => period.endUtc)
      .filter((time) => isValidUtcClock(time))
      .sort();
    return validEnds[validEnds.length - 1] ?? '22:00';
  }, [state.operatingPeriodsUtc]);

  const demandOperatingPeriodsUtc = useMemo(() => {
    if (
      isValidUtcClock(demandServiceStartUtc) &&
      isValidUtcClock(demandServiceEndUtc) &&
      toUtcClockMinutes(demandServiceEndUtc) > toUtcClockMinutes(demandServiceStartUtc)
    ) {
      return [
        {
          id: 'demand_window',
          startUtc: demandServiceStartUtc,
          endUtc: demandServiceEndUtc,
        },
      ];
    }
    return state.operatingPeriodsUtc;
  }, [demandServiceEndUtc, demandServiceStartUtc, state.operatingPeriodsUtc]);

  useEffect(() => {
    wizard.clearFleetTimelineSlotStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualTimelineSignature]);

  const handleDemandServiceStartTimeChange = useCallback(
    (startUtc: string) => {
      wizard.setOperatingPeriods([
        {
          id: 'demand_window',
          startUtc,
          endUtc: demandServiceEndUtc,
        },
      ]);
      wizard.setGenerateTripsStatus('idle');
      wizard.setAuthoritativeRideSource(null, 'none');
    },
    [demandServiceEndUtc, wizard]
  );

  const handleDemandServiceEndTimeChange = useCallback(
    (endUtc: string) => {
      wizard.setOperatingPeriods([
        {
          id: 'demand_window',
          startUtc: demandServiceStartUtc,
          endUtc,
        },
      ]);
      wizard.setGenerateTripsStatus('idle');
      wizard.setAuthoritativeRideSource(null, 'none');
    },
    [demandServiceStartUtc, wizard]
  );

  useEffect(() => {
    if (!open || currentStepId !== 'fleet') {
      return;
    }
    if (state.fleetSource !== 'none' || state.fleetDefaultsSeededFromDemand) {
      return;
    }
    if (typeof state.demandTrips !== 'number' || state.demandTrips <= 0) {
      return;
    }

    const totalServiceMinutes = state.operatingPeriodsUtc.reduce((sum, period) => {
      if (!isValidUtcClock(period.startUtc) || !isValidUtcClock(period.endUtc)) {
        return sum;
      }
      const start = parseTime(period.startUtc);
      const end = parseTime(period.endUtc);
      if (end <= start) {
        return sum;
      }
      return sum + calculateTimeDifference(start, end);
    }, 0);

    if (totalServiceMinutes <= 0) {
      return;
    }

    const totalServiceHours = totalServiceMinutes / 60;
    const tripsPerVehiclePerHour = getTripsPerVehiclePerHourForPreset(state.servicePreset);
    const estimatedVehicleCount = Math.max(
      1,
      Math.ceil(state.demandTrips / (totalServiceHours * tripsPerVehiclePerHour))
    );
    if (estimatedVehicleCount === state.vehicleCount) {
      wizard.setFleetDefaultsSeededFromDemand(true);
      return;
    }
    wizard.setVehicleCount(estimatedVehicleCount);
    wizard.setFleetDefaultsSeededFromDemand(true);
  }, [
    currentStepId,
    open,
    state.demandTrips,
    state.fleetDefaultsSeededFromDemand,
    state.fleetSource,
    state.operatingPeriodsUtc,
    state.servicePreset,
    state.vehicleCount,
    wizard,
  ]);

  useEffect(() => {
    entrypointRef.current = wizardEntrypoint;
  }, [wizardEntrypoint]);

  wizardStateRef.current = state;
  polygonCountRef.current = polygonCount;

  useEffect(() => {
    if (!requiresHubs) {
      wizard.syncHubWeights([]);
      if (state.hubCount !== 0) {
        wizard.setHubCount(0);
      }
      return;
    }
    if (state.hubCount !== hubOptions.length) {
      wizard.setHubCount(hubOptions.length);
    }
    wizard.syncHubWeights(hubOptions);
  }, [hubOptions, requiresHubs, state.hubCount, wizard]);

  useEffect(() => {
    if (state.generateTripsStatus === 'ready' && state.fleetHelpStatus === 'idle') {
      wizard.setFleetHelpStatus('ready');
    }
  }, [state.fleetHelpStatus, state.generateTripsStatus, wizard]);

  useEffect(() => {
    const didResumeTokenChange = mapInteractionResumeToken !== lastResumeTokenRef.current;
    lastResumeTokenRef.current = mapInteractionResumeToken;

    if (didResumeTokenChange && wizardSessionIdRef.current && pendingMapHelperRef.current) {
      const helperType = pendingMapHelperRef.current;
      pendingMapHelperRef.current = null;
      trackWizardHelperComplete({
        wizardSessionId: wizardSessionIdRef.current,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        helperType,
      });
    }

    const shouldAdvanceToHubPlacement =
      didResumeTokenChange &&
      state.isMinimizedForMap &&
      activeDrawTool === 'zone' &&
      currentStepId === 'configuration' &&
      requiresHubs &&
      canProceedToHubPlacement;

    if (shouldAdvanceToHubPlacement) {
      const sessionId = wizardSessionIdRef.current;
      if (sessionId) {
        pendingMapHelperRef.current = 'map_hub_draw';
        trackWizardHelperStart({
          wizardSessionId: sessionId,
          stepIndex: state.currentStep,
          stepId: getWizardStepId(currentStepId),
          helperType: 'map_hub_draw',
        });
      }
      if (!state.serviceMode) {
        wizard.setServiceMode('door_to_hub');
      }
      setHubPlacementMessage(null);
      setActiveDrawTool('hub');
      wizard.beginHubPlacement(pointCount);
      onDrawHub();
      return;
    }

    if (didResumeTokenChange && state.isMinimizedForMap) {
      wizard.setMinimizedForMap(false);
    }
    if (didResumeTokenChange) {
      setActiveDrawTool(null);
    }
  }, [
    activeDrawTool,
    canProceedToHubPlacement,
    currentStepId,
    mapInteractionResumeToken,
    onDrawHub,
    pointCount,
    requiresHubs,
    state.currentStep,
    state.isMinimizedForMap,
    state.serviceMode,
    wizard,
  ]);

  useEffect(() => {
    if (!open) {
      schoolConfigAutoMapStartedRef.current = false;
      return;
    }

    const isSchoolConfiguration = currentStepId === 'configuration' && state.servicePreset === 'school';
    if (!isSchoolConfiguration) {
      schoolConfigAutoMapStartedRef.current = false;
      return;
    }

    if (state.isMinimizedForMap) {
      schoolConfigAutoMapStartedRef.current = true;
      return;
    }

    if (!schoolConfigAutoMapStartedRef.current) {
      schoolConfigAutoMapStartedRef.current = true;
      if (polygonCount <= 0) {
        setActiveDrawTool('zone');
        wizard.setMinimizedForMap(true);
        onDrawPolygon();
        return;
      }
      setActiveDrawTool('hub');
      wizard.beginHubPlacement(pointCount);
      wizard.setMinimizedForMap(true);
      onDrawHub();
    }
  }, [
    currentStepId,
    onDrawHub,
    onDrawPolygon,
    open,
    pointCount,
    polygonCount,
    state.isMinimizedForMap,
    state.servicePreset,
    wizard,
  ]);

  const resetSessionTracking = useCallback(() => {
    wizardSessionIdRef.current = null;
    wizardOpenedAtRef.current = null;
    stepEnteredAtRef.current = null;
    lastStepViewKeyRef.current = null;
    defaultSnapshotRef.current = null;
    changedFieldGroupsRef.current = new Set();
    initialSimulationNameRef.current = '';
    pendingMapHelperRef.current = null;
    demandHelperUsedRef.current = false;
  }, []);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open && !wasOpen) {
      const latestState = wizardStateRef.current;
      const latestPolygonCount = polygonCountRef.current;
      const sessionId = createWizardSessionId();
      wizardSessionIdRef.current = sessionId;
      wizardOpenedAtRef.current = Date.now();
      stepEnteredAtRef.current = Date.now();
      defaultSnapshotRef.current = buildWizardDefaultSnapshot(latestState, latestPolygonCount);
      initialSimulationNameRef.current = latestState.simulationName;
      lastStepViewKeyRef.current = null;
      changedFieldGroupsRef.current = new Set();
      demandHelperUsedRef.current = false;

      trackWizardOpened({
        wizardSessionId: sessionId,
        entrypoint: entrypointRef.current,
        stepId: getWizardStepId(currentStepId),
        stepIndex: latestState.currentStep,
      });
    }

    if (!open && wasOpen) {
      wizard.saveDraft();
      resetSessionTracking();
    }
  }, [open, currentStepId, resetSessionTracking, wizard]);

  useEffect(() => {
    if (!open || !wizardSessionIdRef.current) {
      return;
    }

    const sessionId = wizardSessionIdRef.current;
    const viewKey = `${sessionId}:${currentStepId}`;
    if (lastStepViewKeyRef.current === viewKey) {
      return;
    }
    lastStepViewKeyRef.current = viewKey;
    stepEnteredAtRef.current = Date.now();

    trackWizardStepViewed({
      wizardSessionId: sessionId,
      stepIndex: state.currentStep,
      stepId: getWizardStepId(currentStepId),
      preset: toServicePreset(state.servicePreset),
      serviceMode: toServiceMode(state.serviceMode),
    });
  }, [open, currentStepId, state.currentStep, state.serviceMode, state.servicePreset]);

  useEffect(() => {
    if (!open || !wizardSessionIdRef.current || !defaultSnapshotRef.current) {
      return;
    }
    const sessionId = wizardSessionIdRef.current;
    const baseline = defaultSnapshotRef.current;
    const changedGroups = diffWizardFieldGroups(baseline, state, polygonCount);

    changedGroups.forEach((group) => {
      if (changedFieldGroupsRef.current.has(group)) {
        return;
      }
      changedFieldGroupsRef.current.add(group);
      trackWizardFieldGroupChanged({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        fieldGroup: group,
        changeSource: 'user_input',
      });
    });

    if (
      state.simulationName.trim() !== initialSimulationNameRef.current.trim() &&
      !changedFieldGroupsRef.current.has('simulation_name_field')
    ) {
      changedFieldGroupsRef.current.add('simulation_name_field');
      trackWizardFieldGroupChanged({
        wizardSessionId: sessionId,
        stepIndex: wizard.totalSteps,
        stepId: 'summary',
        fieldGroup: 'simulation_name_field',
        changeSource: 'user_input',
      });
    }
  }, [open, polygonCount, currentStepId, state, wizard.totalSteps]);

  useEffect(() => {
    if (!open) {
      setFleetHelpError(null);
      setFleetHelpResult(null);
      setIsRunLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !state.authoritativeRideLayerId || !isWizardRideLayerIdUsable) {
      return;
    }
    if (!isWizardRideLayerIdUsable(state.authoritativeRideLayerId, true)) {
      wizard.setAuthoritativeRideSource(null, 'none');
    }
  }, [isWizardRideLayerIdUsable, open, state.authoritativeRideLayerId, wizard]);

  const elapsedSessionMs = useCallback(() => {
    if (!wizardOpenedAtRef.current) {
      return 0;
    }
    return Math.max(0, Date.now() - wizardOpenedAtRef.current);
  }, []);

  const activeStepDurationMs = useCallback(() => {
    if (!stepEnteredAtRef.current) {
      return 0;
    }
    return Math.max(0, Date.now() - stepEnteredAtRef.current);
  }, []);

  const finalizeDismiss = useCallback(
    (dismissSource: WizardDismissSource) => {
      const sessionId = wizardSessionIdRef.current;
      if (sessionId) {
        const stepDuration = activeStepDurationMs();
        trackWizardDismissed({
          wizardSessionId: sessionId,
          stepIndex: state.currentStep,
          stepId: getWizardStepId(currentStepId),
          elapsedSessionMs: elapsedSessionMs(),
          dismissSource,
          stepDurationMs: stepDuration,
        });
        emitWizardDefaultRetained({
          wizardSessionId: sessionId,
          completionStatus: 'dismissed',
          changedGroups: changedFieldGroupsRef.current,
        });
      }
      wizard.saveDraft();
      setIsRunLoading(false);
      resetSessionTracking();
      onClose();
    },
    [activeStepDurationMs, currentStepId, elapsedSessionMs, onClose, resetSessionTracking, state.currentStep, wizard]
  );

  const handleDialogClose = useCallback(
    (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      const dismissSource: WizardDismissSource = reason === 'backdropClick' ? 'backdrop' : 'escape';
      finalizeDismiss(dismissSource);
    },
    [finalizeDismiss]
  );

  const handleCloseClick = useCallback(() => {
    finalizeDismiss('close_button');
  }, [finalizeDismiss]);

  const handleResetClick = useCallback(() => {
    wizard.clearDraft();
    wizard.reset();
    setActiveDrawTool(null);
    setFleetHelpError(null);
    setFleetHelpResult(null);
    setShowDemandLimitAttention(false);
  }, [wizard]);

  const handleNextPress = useCallback(async () => {
    const sessionId = wizardSessionIdRef.current;
    if (!sessionId) {
      return;
    }
    const isDemandWorkInFlight = state.demandHelpStatus === 'loading' || state.generateTripsStatus === 'loading';
    if (currentStepId === 'demand' && isDemandWorkInFlight) {
      return;
    }

    if (!wizard.stepValidation.canProceed) {
      if (currentStepId === 'demand' && exceedsDemandTripLimit) {
        setShowDemandLimitAttention(true);
        setDemandLimitFocusSignal((prev) => prev + 1);
        demandOverlayInputRef.current?.focus();
        demandOverlayInputRef.current?.select();
      }
      trackWizardValidationBlocked({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        validationReason: mapWizardValidationReason(wizard.stepValidation.reason),
      });
      return;
    }

    const duration = activeStepDurationMs();
    trackWizardNavigationClicked({
      wizardSessionId: sessionId,
      stepIndex: state.currentStep,
      stepId: getWizardStepId(currentStepId),
      action: 'next',
    });
    trackWizardStepCompleted({
      wizardSessionId: sessionId,
      stepIndex: state.currentStep,
      stepId: getWizardStepId(currentStepId),
      stepDurationMs: duration,
      usedHelper: currentStepId === 'demand' ? demandHelperUsedRef.current : undefined,
    });

    // School flow: jump straight into map design when leaving setup
    // to avoid briefly rendering the next modal page before minimizing.
    if (currentStepId === 'setup' && state.servicePreset === 'school') {
      schoolConfigAutoMapStartedRef.current = true;
      wizard.setStep(state.currentStep + 1);
      if (polygonCount <= 0) {
        setActiveDrawTool('zone');
        wizard.setMinimizedForMap(true);
        onDrawPolygon();
      } else {
        setActiveDrawTool('hub');
        wizard.beginHubPlacement(pointCount);
        wizard.setMinimizedForMap(true);
        onDrawHub();
      }
      return;
    }

    if (currentStepId === 'fleet' && !(state.fleetSource === 'wizard_helper' && state.authoritativeFleetId)) {
      try {
        const discreteManualShifts = editableManualFleetPreview
          ? expandShiftPeriodsToDiscreteShifts(editableManualFleetPreview.shifts, (name, index) =>
              t('fleet.manualShiftSuffix', { name, index })
            )
          : undefined;
        const fleetId = await onCreateManualFleet({
          vehicleCount: state.vehicleCount,
          capacityStandard: state.capacityStandard,
          capacityWheelchair: state.capacityWheelchair,
          shifts: discreteManualShifts,
        });
        wizard.setAuthoritativeFleetSource(fleetId, 'manual');
      } catch (error) {
        wizard.setDemandError(error instanceof Error ? error.message : t('fleet.errorFailedToCreateManualFleet'));
        return;
      }
    }

    wizard.setStep(state.currentStep + 1);
  }, [
    activeStepDurationMs,
    currentStepId,
    exceedsDemandTripLimit,
    onCreateManualFleet,
    onDrawHub,
    onDrawPolygon,
    pointCount,
    polygonCount,
    editableManualFleetPreview,
    state,
    wizard,
  ]);
  const handleRunPress = useCallback(async () => {
    const sessionId = wizardSessionIdRef.current;
    if (!sessionId || isRunLoading) {
      return;
    }

    if (!wizard.stepValidation.canProceed) {
      trackWizardValidationBlocked({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        validationReason: mapWizardValidationReason(wizard.stepValidation.reason),
      });
      return;
    }

    try {
      setIsRunLoading(true);
      // Transition immediately into simulation-results context on run click.
      wizard.saveDraft();
      onClose();
      await onRunSimulation({
        simulationName: state.simulationName.trim(),
        feelLevel: state.feelLevel,
        feelSlope: state.feelSlope,
        feelInterceptMinutes: state.feelInterceptMinutes,
        maxTripTimeMinutes: state.maxTripTimeMinutes,
        authoritativeFleetId: state.authoritativeFleetId,
        authoritativeRideLayerId: state.authoritativeRideLayerId,
      });

      const summaryDuration = activeStepDurationMs();
      trackWizardStepCompleted({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: 'summary',
        stepDurationMs: summaryDuration,
      });
      trackWizardCompleted({
        wizardSessionId: sessionId,
        elapsedSessionMs: elapsedSessionMs(),
        finalStepId: 'summary',
        preset: toServicePreset(state.servicePreset),
        serviceMode: toServiceMode(state.serviceMode),
        stepDurationMs: summaryDuration,
      });
      emitWizardDefaultRetained({
        wizardSessionId: sessionId,
        completionStatus: 'completed',
        changedGroups: changedFieldGroupsRef.current,
      });
      resetSessionTracking();
    } catch (error) {
      // Run errors are surfaced in simulation-results context.
      void error;
    }
  }, [
    activeStepDurationMs,
    currentStepId,
    elapsedSessionMs,
    isRunLoading,
    onClose,
    onRunSimulation,
    resetSessionTracking,
    state,
    wizard,
  ]);

  const handleBackPress = useCallback(() => {
    const sessionId = wizardSessionIdRef.current;
    if (!sessionId || state.currentStep <= 1) {
      return;
    }
    trackWizardNavigationClicked({
      wizardSessionId: sessionId,
      stepIndex: state.currentStep,
      stepId: getWizardStepId(currentStepId),
      action: 'back',
    });
    wizard.setStep(state.currentStep - 1);
  }, [currentStepId, state.currentStep, wizard]);

  const handleDemandHelp = useCallback(async () => {
    const sessionId = wizardSessionIdRef.current;
    if (!sessionId) {
      return;
    }
    if (state.demandHelpStatus === 'loading' || state.generateTripsStatus === 'loading') {
      return;
    }
    const resolveServiceModeForTripGeneration = () => {
      if (state.serviceMode) {
        return state.serviceMode;
      }
      const inferredServiceMode = state.servicePreset === 'school' ? 'door_to_hub' : 'door_to_door';
      wizard.setServiceMode(inferredServiceMode);
      return inferredServiceMode;
    };

    trackWizardHelperStart({
      wizardSessionId: sessionId,
      stepIndex: state.currentStep,
      stepId: getWizardStepId(currentStepId),
      helperType: 'population_analysis',
    });
    wizard.setDemandHelpStatus('loading');
    wizard.setGenerateTripsStatus('loading');
    wizard.setDemandError(null);
    wizard.setDemandHelperStatusText(null);
    setShowDemandLimitAttention(false);
    try {
      const result = await onRequestDemandInsight();
      const insight =
        (result as DemandInsightResult).insight ??
        (result as unknown as {
          totalPopulation: number;
          coveredPopulation: number;
          transitPropensityPct: number;
          estimatedAvgTripLengthKm: number;
        });
      const demandMultiplier = getDemandMultiplierForPreset(state.servicePreset);
      const estimatedDemandTrips = Math.max(
        1,
        Math.round(((insight.totalPopulation * DEMAND_BASE_MULTIPLIER) / 30) * demandMultiplier)
      );
      const generatedTrips = Math.min(estimatedDemandTrips, WIZARD_MAX_GENERATED_TRIPS);
      const exceedsTripGenerationLimit = estimatedDemandTrips > WIZARD_MAX_GENERATED_TRIPS;

      wizard.setDemandInsight(insight);
      wizard.setDemandTrips(generatedTrips);
      demandHelperUsedRef.current = true;
      const serviceModeForGeneration = resolveServiceModeForTripGeneration();
      const passengerDistributionForGeneration = getEffectivePassengerDistribution(
        state.servicePreset,
        state.passengerDistribution
      );

      const rideLayerId = await onTriggerGenerateTripsBackground({
        numberOfRides: generatedTrips,
        servicePreset: state.servicePreset,
        serviceMode: serviceModeForGeneration,
        operatingPeriodsUtc: demandOperatingPeriodsUtc,
        passengerDistribution: passengerDistributionForGeneration,
        hubWeights: state.hubWeights,
        useMeetingPointsOnly: false,
        meetingPointLayerId: null,
      });
      const authoritativeRideLayerId = rideLayerId ?? result.authoritativeRideLayerId ?? null;
      if (!authoritativeRideLayerId) {
        throw new Error(t('errors.tripGenerationNoLayer'));
      }
      wizard.setAuthoritativeRideSource(authoritativeRideLayerId, 'wizard_helper');
      wizard.setGenerateTripsStatus('ready');
      wizard.setDemandHelpStatus('ready');
      wizard.setDemandHelperStatusText(
        exceedsTripGenerationLimit
          ? t('errors.demandHelperStatusExceeds', {
              estimated: estimatedDemandTrips.toLocaleString(),
              generated: generatedTrips.toLocaleString(),
              max: WIZARD_MAX_GENERATED_TRIPS.toLocaleString(),
            })
          : t('errors.demandHelperStatus')
      );

      trackWizardHelperComplete({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        helperType: 'population_analysis',
        becameAuthoritative: Boolean(authoritativeRideLayerId),
        authoritativeRideLayerSet: Boolean(authoritativeRideLayerId),
      });
    } catch (error) {
      wizard.setDemandHelpStatus('error');
      wizard.setGenerateTripsStatus('error');
      wizard.setDemandHelperStatusText(null);
      wizard.setDemandError(error instanceof Error ? error.message : t('errors.unableToEstimateDemand'));
      const { category, code } = categorizeWizardHelperError(error);
      trackWizardHelperFail({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        helperType: 'population_analysis',
        errorCategory: category,
        errorCode: code,
        retryable: category === 'network' || category === 'timeout',
      });
    }
  }, [currentStepId, demandOperatingPeriodsUtc, onRequestDemandInsight, onTriggerGenerateTripsBackground, state, wizard]);

  const handleGenerateTripsPreview = useCallback(async () => {
    const sessionId = wizardSessionIdRef.current;
    if (!sessionId) {
      return;
    }
    if (state.generateTripsStatus === 'loading' || state.demandHelpStatus === 'loading') {
      return;
    }
    if (!state.serviceMode) {
      wizard.setDemandError(t('errors.chooseServiceMode'));
      return;
    }
    if (typeof state.demandTrips !== 'number' || state.demandTrips <= 0) {
      wizard.setDemandError(t('errors.enterExpectedTrips'));
      return;
    }
    if (state.demandTrips > WIZARD_MAX_GENERATED_TRIPS) {
      wizard.setDemandError(t('errors.tripLimitGuard', { max: WIZARD_MAX_GENERATED_TRIPS }));
      return;
    }

    const serviceModeForGeneration = state.serviceMode;
    const passengerDistributionForGeneration = getEffectivePassengerDistribution(
      state.servicePreset,
      state.passengerDistribution
    );

    try {
      wizard.setGenerateTripsStatus('loading');
      wizard.setDemandError(null);
      const rideLayerId = await onTriggerGenerateTripsBackground({
        numberOfRides: Math.min(WIZARD_MAX_GENERATED_TRIPS, Math.max(1, state.demandTrips)),
        servicePreset: state.servicePreset,
        serviceMode: serviceModeForGeneration,
        operatingPeriodsUtc: demandOperatingPeriodsUtc,
        passengerDistribution: passengerDistributionForGeneration,
        hubWeights: state.hubWeights,
        useMeetingPointsOnly: false,
        meetingPointLayerId: null,
      });
      if (!rideLayerId) {
        throw new Error(t('errors.tripGenerationNoLayer'));
      }
      wizard.setGenerateTripsStatus('ready');
      wizard.setAuthoritativeRideSource(rideLayerId, 'wizard_generated');
    } catch (error) {
      wizard.setGenerateTripsStatus('error');
      wizard.setDemandError(error instanceof Error ? error.message : t('errors.failedToGenerateTrips'));
    }
  }, [
    demandOperatingPeriodsUtc,
    onTriggerGenerateTripsBackground,
    state.demandHelpStatus,
    state.generateTripsStatus,
    state.demandTrips,
    state.hubWeights,
    state.passengerDistribution,
    state.serviceMode,
    state.servicePreset,
    wizard,
  ]);

  const handleStepChipClick = useCallback(
    (stepId: Parameters<typeof wizard.setStepById>[0], stepIndex: number) => {
      // Allow direct navigation only to steps that are already unlocked.
      if (stepIndex + 1 > state.currentStep) {
        return;
      }
      wizard.setStepById(stepId);
    },
    [state.currentStep, wizard]
  );

  const handleDrawPolygon = useCallback(() => {
    setHubPlacementMessage(null);
    const sessionId = wizardSessionIdRef.current;
    if (sessionId) {
      pendingMapHelperRef.current = 'map_polygon_draw';
      trackWizardHelperStart({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        helperType: 'map_polygon_draw',
      });
    }
    setActiveDrawTool('zone');
    wizard.setMinimizedForMap(true);
    onDrawPolygon();
  }, [currentStepId, onDrawPolygon, state.currentStep, wizard]);

  const handleFinishZonePolygon = useCallback(() => {
    if (polygonDraftVertexCount < MIN_WIZARD_POLYGON_VERTICES) {
      return;
    }

    const sessionId = wizardSessionIdRef.current;
    if (sessionId) {
      pendingMapHelperRef.current = 'map_hub_draw';
      trackWizardHelperStart({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        helperType: 'map_hub_draw',
      });
    }

    if (!state.serviceMode) {
      wizard.setServiceMode('door_to_hub');
    }

    setActiveDrawTool('hub');
    wizard.beginHubPlacement(pointCount);
    onFinishZonePolygon();
  }, [currentStepId, onFinishZonePolygon, pointCount, polygonDraftVertexCount, state.currentStep, state.serviceMode, wizard]);

  const handleDrawHub = useCallback(() => {
    if (!canProceedToHubPlacement) {
      setHubPlacementMessage(
        t('hubGuidance.drawFirst', { minVertices: MIN_WIZARD_POLYGON_VERTICES })
      );
      return;
    }
    setHubPlacementMessage(null);
    const sessionId = wizardSessionIdRef.current;
    if (sessionId) {
      pendingMapHelperRef.current = 'map_hub_draw';
      trackWizardHelperStart({
        wizardSessionId: sessionId,
        stepIndex: state.currentStep,
        stepId: getWizardStepId(currentStepId),
        helperType: 'map_hub_draw',
      });
    }
    // If a hub is being placed without an explicit mode selection yet,
    // default to the hub-based mode to avoid deadlocking "Done" state.
    if (!state.serviceMode) {
      wizard.setServiceMode('door_to_hub');
    }
    setActiveDrawTool('hub');
    wizard.beginHubPlacement(pointCount);
    wizard.setMinimizedForMap(true);
    onDrawHub();
  }, [
    canProceedToHubPlacement,
    currentStepId,
    onDrawHub,
    pointCount,
    state.currentStep,
    state.serviceMode,
    wizard,
  ]);

  const inferredHubCount = useMemo(
    () => Math.max(state.hubCount, pointCount - (state.hubBaselinePointCount ?? pointCount)),
    [pointCount, state.hubBaselinePointCount, state.hubCount]
  );

  const hasPolygonReadyForCompletion =
    polygonCount > 0 || hasWizardServiceAreaPolygon || polygonDraftVertexCount >= MIN_WIZARD_POLYGON_VERTICES;

  const canCompleteMapDesign =
    currentStepId !== 'configuration' ||
    wizard.stepValidation.canProceed ||
    (state.serviceMode === 'door_to_door' && hasPolygonReadyForCompletion) ||
    (polygonCount > 0 && inferredHubCount > 0 && !state.serviceMode);

  const handleCompleteMapDesign = useCallback(() => {
    if (!canCompleteMapDesign) {
      return;
    }
    wizard.setMinimizedForMap(false);
    wizard.syncHubCountFromPoints(pointCount);
    if ((state.serviceMode === 'door_to_door' || !state.serviceMode) && inferredHubCount > 0) {
      wizard.setServiceMode('door_to_hub');
    }
    if (currentStepId === 'configuration') {
      wizard.setStepById('configuration');
    }
    setActiveDrawTool(null);
    onDoneMapInteraction();
  }, [canCompleteMapDesign, currentStepId, inferredHubCount, onDoneMapInteraction, pointCount, state.serviceMode, wizard]);

  const shouldShowEditPolygonAction =
    Boolean(state.demandError) &&
    /(polygon is too large|polygon is too detailed|maximum supported size|limit is)/i.test(state.demandError ?? '');

  const handleEditPolygonFromDemandError = useCallback(() => {
    wizard.setMinimizedForMap(true);
    setActiveDrawTool(null);
    onEditServiceAreaPolygon();
  }, [onEditServiceAreaPolygon, wizard]);

  const currentStepContent = () => {
    if (currentStepId === 'setup') {
      return (
        <SetupStep
          selectedPreset={state.servicePreset}
          onSelectPreset={wizard.setServicePreset}
        />
      );
    }

    if (currentStepId === 'configuration') {
      return (
        <ConfigurationStep
          serviceMode={state.serviceMode}
          requiresHubs={requiresHubs}
          hubCount={state.hubCount}
          onSelectMode={wizard.setServiceMode}
          polygonCount={polygonCount}
          serviceAreaPolygonLabel={serviceAreaPolygonLabel}
          onDrawPolygon={handleDrawPolygon}
        />
      );
    }

    if (currentStepId === 'demand') {
      return (
        <DemandEstimationStep
          demandTrips={state.demandTrips}
          serviceStartTimeUtc={demandServiceStartUtc}
          serviceEndTimeUtc={demandServiceEndUtc}
          demandHelpStatus={state.demandHelpStatus}
          demandInsight={state.demandInsight}
          demandError={state.demandError}
          helperStatusText={state.demandHelperStatusText}
          maxGeneratedTrips={WIZARD_MAX_GENERATED_TRIPS}
          focusDemandInputSignal={demandLimitFocusSignal}
          emphasizeLimitWarning={showDemandLimitAttention}
          onDemandTripsChange={(value) => {
            wizard.setDemandTrips(value);
            wizard.setGenerateTripsStatus('idle');
            wizard.setAuthoritativeRideSource(null, 'none');
            wizard.setDemandHelperStatusText(null);
            setShowDemandLimitAttention(false);
          }}
          onServiceStartTimeChange={handleDemandServiceStartTimeChange}
          onServiceEndTimeChange={handleDemandServiceEndTimeChange}
          onHelpMe={handleDemandHelp}
          onGenerateTrips={handleGenerateTripsPreview}
          onEditPolygonOnMap={handleEditPolygonFromDemandError}
          showEditPolygonOnMapAction={shouldShowEditPolygonAction}
        />
      );
    }
    if (currentStepId === 'hub_weighting') {
      return <HubWeightingStep hubWeights={state.hubWeights} onHubWeightChange={wizard.setHubWeight} />;
    }

    if (currentStepId === 'fleet') {
      return (
        <FleetSupplyStep
          vehicleCount={state.vehicleCount}
          capacityStandard={state.capacityStandard}
          capacityWheelchair={state.capacityWheelchair}
          onVehicleCountChange={(value) => {
            wizard.setVehicleCount(value);
            if (state.fleetSource === 'wizard_helper') {
              setFleetHelpResult(null);
              setFleetHelpError(null);
            }
          }}
          onCapacityStandardChange={(value) => {
            wizard.setCapacityStandard(value);
            if (state.fleetSource === 'wizard_helper') {
              setFleetHelpResult(null);
              setFleetHelpError(null);
            }
          }}
          onCapacityWheelchairChange={(value) => {
            wizard.setCapacityWheelchair(value);
            if (state.fleetSource === 'wizard_helper') {
              setFleetHelpResult(null);
              setFleetHelpError(null);
            }
          }}
          canOpenHelpMe={state.generateTripsStatus === 'ready' || Boolean(state.authoritativeRideLayerId)}
          helpMeStatus={state.generateTripsStatus === 'loading' ? 'loading' : state.fleetHelpStatus}
          helpMeError={fleetHelpError}
          helpResult={fleetHelpResult}
          manualPreview={editableManualFleetPreview}
          fleetMode={state.fleetSource}
          authoritativeFleetId={state.authoritativeFleetId}
          validationError={
            currentStepId === 'fleet' && !wizard.stepValidation.canProceed && wizard.stepValidation.reason
              ? t(wizard.stepValidation.reason, {
                  defaultValue: wizard.stepValidation.reason,
                  max: WIZARD_MAX_GENERATED_TRIPS,
                })
              : undefined
          }
          onTimelineSlotToggle={state.fleetSource === 'wizard_helper' ? undefined : handleTimelineSlotToggle}
          onOpenHelpMe={async () => {
            const sessionId = wizardSessionIdRef.current;
            if (sessionId) {
              trackWizardHelperStart({
                wizardSessionId: sessionId,
                stepIndex: state.currentStep,
                stepId: getWizardStepId(currentStepId),
                helperType: 'auto_fleet_generator',
              });
            }
            setFleetHelpError(null);
            wizard.setFleetHelpStatus('loading');
            try {
              const currentStandard = Math.max(0, Math.round(Number(state.capacityStandard) || 0));
              const currentWheelchair = Math.max(0, Math.round(Number(state.capacityWheelchair) || 0));
              const needsCapacityFallback = currentStandard + currentWheelchair <= 0;
              const effectiveCapacities = needsCapacityFallback
                ? getFallbackFleetCapacities(state.servicePreset)
                : { standard: currentStandard, wheelchair: currentWheelchair };

              if (needsCapacityFallback) {
                wizard.setCapacityStandard(effectiveCapacities.standard);
                wizard.setCapacityWheelchair(effectiveCapacities.wheelchair);
              }

              const result = await onGenerateFleetFromDemand({
                authoritativeRideLayerId: state.authoritativeRideLayerId,
                servicePreset: state.servicePreset,
                capacityStandard: effectiveCapacities.standard,
                capacityWheelchair: effectiveCapacities.wheelchair,
              });
              setFleetHelpResult(result);
              wizard.setVehicleCount(result.summary.totalVehicles);
              const seatsPerVehicleStandard =
                result.summary.totalVehicles > 0
                  ? Math.round(result.summary.totalStandardSeats / result.summary.totalVehicles)
                  : state.capacityStandard;
              const seatsPerVehicleWheelchair =
                result.summary.totalVehicles > 0
                  ? Math.round(result.summary.totalWheelchairSeats / result.summary.totalVehicles)
                  : state.capacityWheelchair;
              wizard.setCapacityStandard(seatsPerVehicleStandard);
              wizard.setCapacityWheelchair(seatsPerVehicleWheelchair);
              wizard.setAuthoritativeFleetSource(result.fleetId, 'wizard_helper');
              wizard.setFleetHelpStatus('ready');

              if (sessionId) {
                trackWizardHelperComplete({
                  wizardSessionId: sessionId,
                  stepIndex: state.currentStep,
                  stepId: getWizardStepId(currentStepId),
                  helperType: 'auto_fleet_generator',
                  becameAuthoritative: true,
                  authoritativeFleetSet: true,
                });
                if (!changedFieldGroupsRef.current.has('fleet_helper_usage')) {
                  changedFieldGroupsRef.current.add('fleet_helper_usage');
                  trackWizardFieldGroupChanged({
                    wizardSessionId: sessionId,
                    stepIndex: state.currentStep,
                    stepId: getWizardStepId(currentStepId),
                    fieldGroup: 'fleet_helper_usage',
                    changeSource: 'helper',
                  });
                }
              }
            } catch (error) {
              wizard.setFleetHelpStatus('error');
              const message = error instanceof Error ? error.message : t('fleet.errorUnableToAutoGenerate');
              setFleetHelpError(message);
              if (sessionId) {
                const { category, code } = categorizeWizardHelperError(error);
                trackWizardHelperFail({
                  wizardSessionId: sessionId,
                  stepIndex: state.currentStep,
                  stepId: getWizardStepId(currentStepId),
                  helperType: 'auto_fleet_generator',
                  errorCategory: category,
                  errorCode: code,
                  retryable: category === 'network' || category === 'timeout',
                });
              }
            }
          }}
        />
      );
    }

    if (currentStepId === 'service_configuration') {
      return (
        <ServiceFeelStep
          feelLevel={state.feelLevel}
          feelSlope={state.feelSlope}
          feelInterceptMinutes={state.feelInterceptMinutes}
          maxTripTimeMinutes={state.maxTripTimeMinutes}
          onFeelLevelChange={wizard.setFeelLevel}
          onMaxTripTimeChange={wizard.setMaxTripTimeMinutes}
        />
      );
    }

    return (
      <SummaryRunStep
        servicePreset={state.servicePreset ?? ''}
        polygonCount={polygonCount}
        operatingPeriods={state.operatingPeriodsUtc}
        serviceMode={state.serviceMode}
        demandTrips={state.demandTrips}
        vehicleCount={state.vehicleCount}
        capacityStandard={state.capacityStandard}
        capacityWheelchair={state.capacityWheelchair}
        feelLevel={state.feelLevel}
        feelSlope={state.feelSlope}
        feelInterceptMinutes={state.feelInterceptMinutes}
        maxTripTimeMinutes={state.maxTripTimeMinutes}
        simulationName={state.simulationName}
        onSimulationNameChange={wizard.setSimulationName}
      />
    );
  };

  const isNextLoading = state.generateTripsStatus === 'loading';
  const canProceed = wizard.stepValidation.canProceed;

  return (
    <>
      <Dialog
        open={open && !state.isMinimizedForMap && !isDemandOverlayStep}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="lg"
        sx={{ zIndex: 2400 }}
        TransitionComponent={Fade}
        transitionDuration={250}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
          },
        }}
        PaperProps={{
          sx: (theme) => ({
            zIndex: 2400,
            borderRadius: 3,
            backgroundColor: opsColors.neutral.panel,
            border: `1px solid ${opsColors.neutral.line}`,
            height: 'min(860px, calc(100vh - 48px))',
            maxHeight: 'calc(100vh - 48px)',
            minHeight: 540,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }),
        }}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${opsColors.neutral.line}`,
          })}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('wizard.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'actionBrand.primary', fontWeight: 600 }}>
            {progressLabel}
          </Typography>
          <Tooltip title={t('wizard.closeWizard')}>
            <IconButton onClick={handleCloseClick} aria-label={t('wizard.closeWizardAriaLabel')}>
              <Icon name="xmark" size={20} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ px: 3, pt: 1.5, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'start', gap: 1.5 }}>
          <Box />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {wizard.steps.map((step, index) => {
              const stepLabel = t(`steps.${step.id}`, { defaultValue: step.label });
              return (
                <Button
                  key={step.id}
                  variant="text"
                  size="small"
                  onClick={() => handleStepChipClick(step.id, index)}
                  disabled={!step.enabled}
                  sx={(theme) => ({
                    px: 1,
                    py: 0.5,
                    borderRadius: 10,
                    border: `1px solid ${opsColors.neutral.line}`,
                    opacity: step.enabled ? 1 : 0.45,
                    backgroundColor:
                      index + 1 === state.currentStep ? opsColors.neutral.panelAlt : opsColors.neutral.panel,
                    textTransform: 'none',
                    minWidth: 0,
                    color: 'text.primary',
                    justifyContent: 'flex-start',
                  })}
                  aria-label={t('wizard.goToStep', { label: stepLabel })}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t('wizard.stepIndexedLabel', { index: index + 1, label: stepLabel })}
                  </Typography>
                </Button>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" variant="text" onClick={handleResetClick}>
              {t('wizard.reset')}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 3,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {currentStepContent()}
        </Box>

        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${opsColors.neutral.line}`,
          })}
        >
          <Button variant="text" onClick={handleBackPress} disabled={state.currentStep === 1}>
            {t('wizard.back')}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {!canProceed && wizard.stepValidation.reason ? (
              <Typography variant="caption" color="text.secondary">
                {t(wizard.stepValidation.reason, { defaultValue: wizard.stepValidation.reason, max: WIZARD_MAX_GENERATED_TRIPS })}
              </Typography>
            ) : null}
            {currentStepId === 'summary' ? (
              <Button
                variant="contained"
                onClick={() => void handleRunPress()}
                disabled={isRunLoading}
                aria-disabled={!canProceed || isRunLoading}
                data-testid="wizard.summary.run-simulation"
              >
                {isRunLoading ? t('wizard.running') : t('wizard.runSimulation')}
              </Button>
            ) : (
              <Button
                variant={canProceed ? 'contained' : 'outlined'}
                onClick={() => void handleNextPress()}
                disabled={!canProceed || isNextLoading}
                aria-disabled={!canProceed || isNextLoading}
                data-testid="wizard.navigation.next"
                sx={
                  canProceed
                    ? undefined
                    : (theme) => ({
                        color: opsColors.neutral.inkSoft,
                        borderColor: opsColors.neutral.line,
                        backgroundColor: opsColors.neutral.panel,
                      })
                }
              >
                {t('wizard.next')}
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      {isDemandOverlayStep ? (
        <Paper
          elevation={6}
          sx={(theme) => ({
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2401,
            borderRadius: 2.5,
            px: 2.5,
            py: 1.75,
            display: 'grid',
            gap: 1.25,
            border: `1px solid ${opsColors.neutral.line}`,
            bgcolor: opsColors.neutral.panel,
            width: 'min(980px, calc(100vw - 32px))',
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'actionBrand.primary' }}>
              {t('demand.wizardStepLabel', { current: state.currentStep, total: wizard.totalSteps })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('demand.overlayTitle')}
              </Typography>
              <Tooltip title={t('wizard.closeWizard')}>
                <IconButton size="small" onClick={handleCloseClick} aria-label={t('wizard.closeWizardAriaLabel')}>
                  <Icon name="xmark" size={20} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              label={t('demand.serviceStartTime')}
              type="time"
              value={demandServiceStartUtc}
              onChange={(event) => handleDemandServiceStartTimeChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              sx={{ width: { xs: '100%', sm: 190 } }}
            />
            <TextField
              label={t('demand.serviceEndTime')}
              type="time"
              value={demandServiceEndUtc}
              onChange={(event) => handleDemandServiceEndTimeChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              sx={{ width: { xs: '100%', sm: 190 } }}
            />
            <TextField
              label={t('demand.expectedTripsPerDay')}
              type="number"
              inputRef={demandOverlayInputRef}
              value={state.demandTrips ?? ''}
              onChange={(event) => {
                const value = event.target.value.trim();
                wizard.setDemandTrips(value ? Math.max(0, Number(value)) : null);
                setShowDemandLimitAttention(false);
              }}
              error={exceedsDemandTripLimit}
              helperText={
                exceedsDemandTripLimit
                  ? t('demand.tripLimitHelper', { max: WIZARD_MAX_GENERATED_TRIPS })
                  : undefined
              }
              sx={(theme) => ({
                width: { xs: '100%', sm: 220 },
                minWidth: { sm: 220 },
                ...(showDemandLimitAttention && exceedsDemandTripLimit
                  ? {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: opsColors.semantic.warn.soft,
                        '& fieldset': {
                          borderColor: opsColors.semantic.warn.color,
                          borderWidth: 2,
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.warning.dark,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.warning.dark,
                        },
                      },
                    }
                  : {}),
              })}
            />
            <Button
              variant="contained"
              onClick={() => void handleGenerateTripsPreview()}
              disabled={
                state.demandHelpStatus === 'loading' ||
                typeof state.demandTrips !== 'number' ||
                state.demandTrips <= 0 ||
                state.demandTrips > WIZARD_MAX_GENERATED_TRIPS
              }
              sx={{ alignSelf: 'center' }}
            >
              {t('demand.generateTrips')}
            </Button>
          </Box>

          <Divider />

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'grid', gap: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('demand.needHelpHeading')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('demand.needHelpDescriptionShort')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => void handleDemandHelp()}
                disabled={state.demandHelpStatus === 'loading'}
                data-testid="wizard.demand.estimate-for-me"
              >
                {state.demandHelpStatus === 'loading' ? t('demand.working') : t('demand.estimateForMe')}
              </Button>
            </Box>
          </Box>

          {state.demandError ? (
            <Alert
              severity="warning"
              action={
                shouldShowEditPolygonAction ? (
                  <Button color="inherit" size="small" onClick={handleEditPolygonFromDemandError}>
                    {t('demand.editPolygonOnMap')}
                  </Button>
                ) : undefined
              }
            >
              {state.demandError}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
            <Button variant="text" onClick={handleBackPress} disabled={state.currentStep === 1}>
              {t('wizard.back')}
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              {!canProceed && wizard.stepValidation.reason ? (
                <Typography variant="caption" color="text.secondary">
                  {t(wizard.stepValidation.reason, { defaultValue: wizard.stepValidation.reason, max: WIZARD_MAX_GENERATED_TRIPS })}
                </Typography>
              ) : null}
              <Button
                variant={canProceed ? 'contained' : 'outlined'}
                onClick={() => void handleNextPress()}
                disabled={!canProceed || isNextLoading}
                aria-disabled={!canProceed || isNextLoading}
                data-testid="wizard.navigation.next"
              >
                {t('wizard.next')}
              </Button>
            </Box>
          </Box>
        </Paper>
      ) : null}

      {open && state.isMinimizedForMap ? (
        <>
          {showHubPlacementGuidance ? (
            <Alert
              severity={hubPlacementMessage ? 'warning' : 'info'}
              sx={{
                position: 'fixed',
                bottom: 84,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2402,
                width: 'min(760px, calc(100vw - 32px))',
                textAlign: 'center',
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              {hubPlacementGuidanceText}
            </Alert>
          ) : null}
          <Paper
            elevation={5}
            sx={(theme) => ({
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2401,
              borderRadius: 999,
              px: 2.5,
              py: 1.25,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.25,
              border: `1px solid ${opsColors.neutral.line}`,
              bgcolor: opsColors.neutral.panel,
              width: 'auto',
            })}
          >
            <>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: 'actionBrand.primary', mr: 1, fontSize: '0.95rem' }}
              >
                {t('mapControls.wizardStep', { current: state.currentStep, total: wizard.totalSteps })}
              </Typography>
              {currentStepId === 'configuration' ? (
                <>
                  <Button
                    size="medium"
                    variant={activeDrawTool === 'zone' ? 'contained' : 'outlined'}
                    startIcon={<Icon d={ICON_FALLBACKS.Pentagon} size={24} />}
                    onClick={polygonDraftVertexCount >= MIN_WIZARD_POLYGON_VERTICES ? handleFinishZonePolygon : handleDrawPolygon}
                    data-testid="wizard.map-controls.draw-zone"
                    sx={
                      activeDrawTool === 'zone'
                        ? {
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            px: 1.5,
                          }
                        : (theme) => ({
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            px: 1.5,
                            color: opsColors.brand.primary,
                            borderColor: opsColors.brand.primary,
                            '&:hover': {
                              backgroundColor: opsColors.brand.primary,
                              color: '#ffffff',
                              borderColor: opsColors.brand.primary,
                            },
                          })
                    }
                  >
                    {polygonDraftVertexCount >= MIN_WIZARD_POLYGON_VERTICES ? t('mapControls.finishZone') : t('mapControls.drawZone')}
                  </Button>
                  <Button
                    size="medium"
                    variant={activeDrawTool === 'hub' ? 'contained' : 'outlined'}
                    startIcon={<Icon d={ICON_FALLBACKS.MyLocation} size={24} />}
                    onClick={handleDrawHub}
                    data-testid="wizard.map-controls.draw-hub"
                    sx={
                      activeDrawTool === 'hub'
                        ? {
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            px: 1.5,
                          }
                        : (theme) => ({
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            px: 1.5,
                            color: opsColors.brand.primary,
                            borderColor: opsColors.brand.primary,
                            '&:hover': {
                              backgroundColor: opsColors.brand.primary,
                              color: '#ffffff',
                              borderColor: opsColors.brand.primary,
                            },
                          })
                    }
                  >
                    {t('mapControls.placeHub')}
                  </Button>
                </>
              ) : null}
              {currentStepId === 'configuration' && requiresHubs ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                  {t('mapControls.hubsLabel', { count: state.hubCount })}
                </Typography>
              ) : null}
              <Button
                size="medium"
                variant={canCompleteMapDesign ? 'contained' : 'outlined'}
                onClick={handleCompleteMapDesign}
                disabled={!canCompleteMapDesign}
                data-testid="wizard.map-controls.done"
                sx={{ fontSize: '0.95rem', fontWeight: 600, px: 1.75 }}
              >
                {t('mapControls.done')}
              </Button>
              <Button
                size="medium"
                variant="text"
                onClick={() => {
                  pendingMapHelperRef.current = null;
                  wizard.setMinimizedForMap(false);
                  setActiveDrawTool(null);
                  onCancelMapInteraction();
                }}
                data-testid="wizard.map-controls.cancel"
                sx={{ fontSize: '0.95rem', fontWeight: 600, px: 1.25 }}
              >
                {t('mapControls.cancel')}
              </Button>
              <Tooltip title={t('wizard.closeWizard')}>
                <IconButton size="small" onClick={handleCloseClick} aria-label={t('wizard.closeWizardAriaLabel')}>
                  <Icon name="xmark" size={20} />
                </IconButton>
              </Tooltip>
            </>
          </Paper>
        </>
      ) : null}

    </>
  );
}
